import imageLoaderSanity from './imageLoaderSanity';
import sanityClient, { hasSanityConfig } from '../sanityClient';

const DEFAULT_PROJECT_TYPES = ['ff87c3', 'project'];
const DEFAULT_ABOUT_TYPES = ['about'];
const DEFAULT_ABOUT_SLUGS = ['about'];

const PORTFOLIO_QUERY = `*[_type in $types && !(_id in path("drafts.**"))]{
  _id,
  _type,
  _updatedAt,
  projectTitle,
  title,
  projectUrl,
  externalUrl,
  url,
  projectDescription,
  description,
  projectYear,
  year,
  projectDescriptionRichText,
  descriptionRich,
  body,
  pageUrl,
  "slugValue": slug.current,
  projectTags,
  tags,
  projectPicture{
    ...,
    asset->{
      _id,
      url,
      metadata {
        dimensions
      }
    }
  },
  image{
    ...,
    asset->{
      _id,
      url,
      metadata {
        dimensions
      }
    }
  },
  mainImage{
    ...,
    asset->{
      _id,
      url,
      metadata {
        dimensions
      }
    }
  },
  picture{
    ...,
    asset->{
      _id,
      url,
      metadata {
        dimensions
      }
    }
  }
}`;

const splitList = (value, fallback) => {
    if (!value) return fallback;

    const parsed = value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

    return parsed.length > 0 ? parsed : fallback;
};

const firstDefined = (...values) => values.find((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
});

const toPortableText = (value) => {
    if (!Array.isArray(value) || value.length === 0) return null;
    return value.some((block) => block && typeof block === 'object' && block._type)
        ? value
        : null;
};

const toPlainText = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
};

const normalizeSlug = (value) => (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_/]/g, '');

const getDocumentTitle = (document) => firstDefined(document.projectTitle, document.title);

const getDocumentImage = (document) => firstDefined(
    document.projectPicture,
    document.image,
    document.mainImage,
    document.picture,
);

const isAboutDocument = (document, aboutTypes, aboutSlugs) => {
    const rawSlug = firstDefined(
        document.pageUrl,
        document.slugValue,
        getDocumentTitle(document),
    );
    const normalizedSlug = normalizeSlug(rawSlug);

    return aboutTypes.includes(document._type)
        || aboutSlugs.includes(normalizedSlug)
        || normalizedSlug === 'about';
};

const formatDocument = (document) => {
    // Validate document exists and has required fields
    if (!document || typeof document !== 'object') return null;

    const title = getDocumentTitle(document);
    const rawSlug = firstDefined(document.pageUrl, document.slugValue, title);
    const imageSource = getDocumentImage(document);

    // Ensure we have at least a title or image
    if (!title && !imageSource) return null;

    return {
        id: document._id || '',
        title: title || '',
        url: firstDefined(document.projectUrl, document.externalUrl, document.url) || '',
        description: firstDefined(
            toPlainText(document.projectDescription),
            toPlainText(document.description),
        ) || '',
        descriptionRich: firstDefined(
            toPortableText(document.projectDescriptionRichText),
            toPortableText(document.descriptionRich),
            toPortableText(document.body),
            toPortableText(document.projectDescription),
            toPortableText(document.description),
        ) || [],
        picture: imageLoaderSanity(imageSource) || '',
        lowResPicture: imageLoaderSanity(imageSource) || '',
        year: firstDefined(document.projectYear, document.year) || null,
        slug: normalizeSlug(rawSlug) || '',
        tags: firstDefined(document.projectTags, document.tags) || [],
        updatedAt: document._updatedAt || null,
    };
};

const preloadProjectImages = (projects) => {
    if (typeof window === 'undefined') return;

    projects.forEach((project) => {
        if (!project.picture) return;

        const image = new window.Image();
        image.src = project.picture;
    });
};

const parseYearValue = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value !== 'string') return null;

    const match = value.match(/\d{4}/);
    if (!match) return null;

    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
};

const sortProjectsByYearDesc = (projects) => [...projects].sort((a, b) => {
    const yearA = parseYearValue(a.year);
    const yearB = parseYearValue(b.year);

    if (yearA !== null && yearB !== null && yearA !== yearB) {
        return yearB - yearA;
    }

    if (yearA !== null && yearB === null) return -1;
    if (yearA === null && yearB !== null) return 1;

    const updatedA = Date.parse(a.updatedAt || '') || 0;
    const updatedB = Date.parse(b.updatedAt || '') || 0;
    return updatedB - updatedA;
});

export const fetchPortfolioContent = async () => {
    if (!hasSanityConfig || !sanityClient) {
        console.warn(
            'Sanity is not configured. Set REACT_APP_SANITY_PROJECT_ID and REACT_APP_SANITY_DATASET to load content.',
        );
        return { aboutProject: null, projects: [] };
    }

    const projectTypes = splitList(
        process.env.REACT_APP_SANITY_PROJECT_TYPES,
        DEFAULT_PROJECT_TYPES,
    );
    const aboutTypes = splitList(
        process.env.REACT_APP_SANITY_ABOUT_TYPES,
        DEFAULT_ABOUT_TYPES,
    );
    const aboutSlugs = splitList(
        process.env.REACT_APP_SANITY_ABOUT_SLUGS,
        DEFAULT_ABOUT_SLUGS,
    );
    const types = [...new Set([...projectTypes, ...aboutTypes])];

    const documents = await sanityClient.fetch(PORTFOLIO_QUERY, { types });

    // Validate documents is an array
    if (!Array.isArray(documents)) {
        console.warn('Expected documents array from Sanity query, received:', typeof documents);
        return { aboutProject: null, projects: [] };
    }

    const aboutDocument = documents.find((document) => isAboutDocument(document, aboutTypes, aboutSlugs)) || null;
    const projects = documents
        .filter((document) => !isAboutDocument(document, aboutTypes, aboutSlugs))
        .map(formatDocument)
        .filter((document) => document && (document.title || document.picture))
        ;

    const sortedProjects = sortProjectsByYearDesc(projects);

    preloadProjectImages(sortedProjects);

    return {
        aboutProject: aboutDocument ? formatDocument(aboutDocument) : null,
        projects: sortedProjects,
    };
};